import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import React, { useState, useEffect } from 'react';
import { auth, isFirebasePopupDismissal } from 'shared/config/firebase';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from 'shared/services/api/axiosInstance';
import preloadData from 'shared/utils/preloadService';
import { markWorkspaceSetupPending } from 'shared/utils/workspaceSetupPending';
import { fetchAndApplySubscriptionForDefaultTeam, syncTrodoIdentifyAndTeam } from 'shared/utils/authSessionBootstrap';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import { useAppDialog } from 'shared/context/AppDialogContext';
import GoogleAuthButton from 'features/onboarding/components/GoogleAuthButton';
import PasswordRequirements from 'features/onboarding/components/PasswordRequirements';
import { evaluatePassword } from 'shared/utils/passwordPolicy';

const ACCENT = 'var(--trodo-primary)';

const inputClass =
  'w-full px-4 py-[10px] bg-[color:var(--surface-base)] border border-[color:var(--border-default)] rounded-lg text-[color:var(--text-primary)] placeholder-[color:var(--text-disabled)] focus:outline-none focus:ring-2 focus:ring-[color:rgb(var(--trodo-primary-rgb)/0.18)] focus:border-[color:var(--trodo-primary)] focus:shadow-[0_0_0_4px_rgb(var(--trodo-primary-rgb)/0.06)] text-[14px] transition-shadow';

function SignupForm({ onBackToLogin, toggleLoading }) {
  const { alert: appAlert } = useAppDialog();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isOtpView, setIsOtpView] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }

    const storedUser = localStorage.getItem('User');
    const accessToken = localStorage.getItem('accessToken');
    if (storedUser && accessToken) {
      try {
        const user = JSON.parse(storedUser);
        if (!user.is_verified && user.email) {
          setEmail(user.email);
          setIsOtpView(true);
        }
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
  }, [searchParams]);

  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

  // Recomputed as they type. Name and email are passed because the backend's
  // checkCommonPatterns rejects a password containing either — leaving those
  // out would just move the surprise rather than remove it.
  //
  // Declared ABOVE the effect that reads it: a dependency array is evaluated
  // during render, so referencing it from further down the file is a temporal
  // dead zone crash, not a hoisting nicety.
  const passwordCheck = evaluatePassword(password, { name: fullName, email });

  useEffect(() => {
    const emailOk = email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const nameOk = firstName.trim() !== '' && lastName.trim() !== '';
    // The server's rules, evaluated here, so the button cannot offer to submit
    // something that is going to be refused. `length >= 6` used to pass a
    // password the backend would never accept.
    setIsFormValid(emailOk && nameOk && passwordCheck.valid);
  }, [email, firstName, lastName, passwordCheck.valid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    toggleLoading(true);
    trackClientEvent('sign_up_attempted', { signup_method: 'password' }, { category: 'auth' });

    /**
     * Take the overlay down the moment the request settles — BEFORE anything
     * that waits on the user.
     *
     * This is what produced the "infinite loading" report. The overlay
     * (WorkspaceSetupOverlay) is `fixed inset-0 z-[10000]`; the app's dialogs
     * render at z-1400. So `await appAlert(...)` inside the catch drew the
     * error *behind* the overlay, where it could not be clicked — the promise
     * never resolved, the `finally` never ran, and the spinner stayed up
     * forever. The user was not stuck loading; they were stuck on an invisible
     * OK button.
     *
     * Raising the dialog above 10000 would only restart the arms race that
     * AppDialogContext's own comment already records losing once at z-300. A
     * loading overlay has no business being up while the app waits for a click.
     */
    let settled = false;
    const stopLoading = () => {
      if (!settled) { settled = true; toggleLoading(false); }
    };

    try {
      const response = await axiosInstance.post('/auth/create', {
        formData: {
          name: fullName,
          email,
          password,
        },
      });
      stopLoading();

      if (response.data.accessToken) {
        localStorage.setItem('accessToken', response.data.accessToken);
      }

      if (response.data.user) {
        localStorage.setItem('User', JSON.stringify(response.data.user));

        if (response.data.requiresVerification || !response.data.user.is_verified) {
          setIsOtpView(true);
          if (response.data.message) {
            console.log(response.data.message);
          }
        } else {
          await appAlert(response.data.message || 'Account created successfully', { title: 'Sign up' });
        }
      } else {
        await appAlert(response.data.message || 'An error occurred. Please try again.', { title: 'Sign up', variant: 'error' });
      }
    } catch (error) {
      // Before any alert, always — this is the path that used to hang.
      stopLoading();
      console.error('Error during signup:', error.response?.data || error.message);
      if (error.response) {
        const errorData = error.response.data;
        const errorMessage = errorData?.message || 'An error occurred. Please try again.';

        if (error.response.status === 400 && errorData?.error === 'USER_EXISTS_VERIFIED') {
          await appAlert('User already exists. Please login instead.', { title: 'Sign up', variant: 'error' });
          onBackToLogin?.();
        } else {
          await appAlert(errorMessage, { title: 'Sign up', variant: 'error' });
        }
      } else {
        await appAlert('An error occurred. Please try again.', { title: 'Sign up', variant: 'error' });
      }
    } finally {
      // Belt and braces: stopLoading() is idempotent, so this only fires if
      // some path above returned without reaching one of the explicit calls.
      stopLoading();
    }
  };

  const handleOtpChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpConfirm = async () => {
    const otpCode = otp.join('');
    if (otpCode.length < 6) {
      await appAlert('Please enter the full 6-digit code.', { title: 'Verification', variant: 'error' });
      return;
    }
    let redirecting = false;
    // See handleSubmit above for why this exists — take the overlay down
    // before anything that waits on a click.
    let settled = false;
    const stopLoading = () => {
      if (!settled) { settled = true; toggleLoading(false); }
    };
    toggleLoading(true);
    trackClientEvent('signup_otp_submitted', {}, { category: 'auth' });
    try {
      const response = await axiosInstance.post('/auth/verifyOTP', {
        email,
        otp: otpCode,
      });

      if (response.status === 200) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('User', JSON.stringify(response.data.user));

        const defaultTeam = response.data.defaultTeam;
        if (defaultTeam) {
          localStorage.setItem('selectedTeam', defaultTeam.name);
          localStorage.setItem('selectedTeamData', JSON.stringify(defaultTeam));
          await Promise.all([fetchAndApplySubscriptionForDefaultTeam(defaultTeam, 'signup OTP'), preloadData(true)]);
        } else {
          localStorage.removeItem('selectedTeam');
          localStorage.removeItem('selectedTeamData');
        }

        // Analytics does not hold up the app. identify and the group calls are
        // three round trips, and awaiting them here put ~1s between "signed in"
        // and the first screen. teamContext identifies again as soon as the
        // landing page mounts, so nothing is lost by letting this run behind
        // the redirect.
        void syncTrodoIdentifyAndTeam(response.data.user, defaultTeam);

        markWorkspaceSetupPending();
        redirecting = true;
        window.location.href = defaultTeam
          ? `/${encodeURIComponent(defaultTeam.name)}/chat`
          : '/new-project';
        return;
      }
      stopLoading();
      setIsOtpView(false);
      await appAlert('Invalid OTP. Please try again.', { title: 'Verification', variant: 'error' });
    } catch (error) {
      stopLoading();
      console.error('OTP Verification Error:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred during OTP verification. Please try again.';
      await appAlert(errorMessage, { title: 'Verification', variant: 'error' });
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => document.getElementById('otp-0')?.focus(), 100);
    } finally {
      if (!redirecting) stopLoading();
    }
  };

  const handleResendOtp = async () => {
    try {
      trackClientEvent('signup_otp_resend_requested', {}, { category: 'auth' });
      const userEmail = JSON.parse(localStorage.getItem('User'))?.email || email;
      const response = await axiosInstance.post('/auth/resend-otp', { email: userEmail });
      if (response.data.message) {
        await appAlert('New OTP has been sent to your email', { title: 'OTP sent' });
        setOtp(['', '', '', '', '', '']);
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      await appAlert('Error resending OTP. Please try again.', { title: 'Error', variant: 'error' });
    }
  };

  const googleSignup = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
      login_hint: '',
      state: encodeURIComponent(JSON.stringify({ origin: window.location.origin })),
    });

    let redirecting = false;
    // See handleSubmit above for why this exists — take the overlay down
    // before anything that waits on a click.
    let settled = false;
    const stopLoading = () => {
      if (!settled) { settled = true; toggleLoading(false); }
    };
    try {
      toggleLoading(true);
      trackClientEvent('google_oauth_clicked', { surface: 'signup' }, { category: 'auth' });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // The server signs in whoever this ID token proves, and ignores the
      // name/email below (kept only so an older API still works mid-deploy).
      const idToken = await user.getIdToken();
      const response = await axiosInstance.post('/auth/google-login', {
        idToken,
        name: user.displayName,
        email: user.email,
        auth_provider_info: user.photoURL,
      });

      if (response.data.user) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('User', JSON.stringify(response.data.user));

        const defaultTeam = response.data.defaultTeam;
        if (defaultTeam) {
          localStorage.setItem('selectedTeam', defaultTeam.name);
          localStorage.setItem('selectedTeamData', JSON.stringify(defaultTeam));
          await Promise.all([fetchAndApplySubscriptionForDefaultTeam(defaultTeam, 'Google signup'), preloadData(true)]);
        } else {
          localStorage.removeItem('selectedTeam');
          localStorage.removeItem('selectedTeamData');
        }

        // Analytics does not hold up the app. identify and the group calls are
        // three round trips, and awaiting them here put ~1s between "signed in"
        // and the first screen. teamContext identifies again as soon as the
        // landing page mounts, so nothing is lost by letting this run behind
        // the redirect.
        void syncTrodoIdentifyAndTeam(response.data.user, defaultTeam);

        markWorkspaceSetupPending();
        redirecting = true;
        window.location.href = defaultTeam
          ? `/${encodeURIComponent(defaultTeam.name)}/chat`
          : '/new-project';
        return;
      }
      stopLoading();
      await appAlert('Could not complete sign up. Please try again.', { title: 'Google', variant: 'error' });
    } catch (error) {
      stopLoading();
      if (!isFirebasePopupDismissal(error)) {
        console.error('Error during Google signup:', error);
        await appAlert('Google sign up failed. Please try again.', { title: 'Google', variant: 'error' });
      }
    } finally {
      if (!redirecting) stopLoading();
    }
  };

  if (isOtpView) {
    return (
      <div className="space-y-6 w-full">
        <p className="text-sm text-[color:var(--text-muted)] -mt-4 mb-2">This should take no longer than 1 minute.</p>
        <h2 className="text-xl font-semibold text-[color:var(--text-primary)]" style={{ fontFamily: 'Inter, sans-serif' }}>
          Enter verification code
        </h2>

        <div className="flex justify-center gap-2 mb-4">
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
              className={`${inputClass} w-11 h-12 text-center text-lg px-0 py-0`}
            />
          ))}
        </div>

        <p className="text-center text-xs text-[color:var(--text-muted)]">
          Didn&apos;t receive a code?{' '}
          <button type="button" onClick={handleResendOtp} className="font-medium" style={{ color: ACCENT }}>
            Resend
          </button>
        </p>

        <button
          type="button"
          onClick={handleOtpConfirm}
          className="w-full py-3.5 rounded-lg font-medium text-ink-onBrand text-[15px] hover:opacity-[0.96] transition-opacity"
          style={{ backgroundColor: ACCENT }}
        >
          Confirm
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5 w-full">
      <p className="text-sm text-[color:var(--text-muted)] -mt-2">We recommend using your work email.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="signup-email" className="sr-only">
            Email
          </label>
          <input
            type="email"
            id="signup-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            placeholder="work@email.com"
            autoComplete="email"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="signup-first" className="sr-only">
              First name
            </label>
            <input
              type="text"
              id="signup-first"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={inputClass}
              placeholder="First name"
              autoComplete="given-name"
              required
            />
          </div>
          <div>
            <label htmlFor="signup-last" className="sr-only">
              Last name
            </label>
            <input
              type="text"
              id="signup-last"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={inputClass}
              placeholder="Last name"
              autoComplete="family-name"
              required
            />
          </div>
        </div>

        {/* No minLength here on purpose. It used to say 6, which was simply
            wrong: the server wants 8 plus four character classes: and a
            browser tooltip cannot express the rest of the policy anyway. The
            rules are listed under the field instead. */}
        <div>
          <label htmlFor="signup-password" className="sr-only">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="signup-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPasswordFocused(true)}
              className={`${inputClass} pr-14`}
              placeholder="Create a secure password"
              autoComplete="new-password"
              aria-describedby="signup-password-requirements"
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <div id="signup-password-requirements">
            <PasswordRequirements
              results={passwordCheck.results}
              blockers={passwordCheck.blockers}
              show={passwordFocused || password.length > 0}
            />
          </div>
        </div>

        <p className="text-[11px] leading-relaxed text-[color:var(--text-muted)] pt-1">
          By clicking &quot;Agree and sign up&quot;, you agree to our{' '}
          <a href="https://trodo.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: ACCENT }}>
            Terms
          </a>{' '}
          and{' '}
          <a href="https://trodo.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: ACCENT }}>
            Privacy Policy
          </a>
          .
        </p>

        <button
          type="submit"
          disabled={!isFormValid}
          className={`group relative overflow-hidden w-full py-[10px] rounded-lg font-medium text-[14px] transition-all duration-200 ${
            isFormValid
              ? 'text-ink-onBrand hover:-translate-y-[1px] hover:shadow-[0_10px_28px_-10px_rgb(var(--trodo-primary-rgb)/0.55)] active:translate-y-0'
              : 'bg-[color:var(--surface-raised)] text-[color:var(--text-muted)] cursor-not-allowed border border-[color:var(--border-default)]'
          }`}
          style={isFormValid ? { backgroundColor: ACCENT } : undefined}
        >
          <span className="relative z-10">Agree and sign up</span>
          {isFormValid && (
            <span
              aria-hidden
              className="absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 translate-x-0 group-hover:translate-x-[300%] transition-transform duration-700 ease-out pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent, rgb(255 255 255 / 0.28), transparent)' }}
            />
          )}
        </button>
      </form>

      {/* ── or divider ── */}
      <div className="relative flex items-center gap-3 my-1">
        <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
        <span className="text-[11px] uppercase tracking-[0.12em] select-none" style={{ color: 'var(--text-muted)' }}>or</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
      </div>

      <GoogleAuthButton variant="signup" onClick={googleSignup} />

      <p className="text-center text-sm text-[color:var(--text-muted)] pt-1">
        Already have an account?{' '}
        <button type="button" onClick={onBackToLogin} className="font-medium" style={{ color: ACCENT }}>
          Log in
        </button>
      </p>
    </div>
  );
}

export default SignupForm;
